import type { StaffProviderHealthEntry } from "@lafayette/schemas";
import { createColumnHelper } from "@tanstack/react-table";
import { formatInstant, shortId } from "../../operations/format";
import { AsyncState } from "../../ui/async-state";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/data-table";
import { Icon } from "../../ui/icon";
import { wrapCells } from "../../ui/wrap-cells";
import type { DiagnosticsCapabilities } from "../access";
import { describeDiagnosticsError } from "../errors";
import {
  linkTypeLabels,
  modeLabels,
  noteLabels,
  paymentEvidenceLabel,
  providerLabels,
} from "../labels";
import type { useProviderHealth } from "../queries";
import { providerRunbook, runbooks } from "../runbooks";
import type { DialogRequest } from "./dialog-state";
import { EvidenceBadge, ProviderStatusBadge } from "./parts";
import { RecordLink } from "./RecordLink";
import { RunbookLink } from "./RunbookLink";
import { dash, hint, Section } from "./Section";

// --- the columns --------------------------------------------------------------------------------------------------------------------

const providerColumn = createColumnHelper<StaffProviderHealthEntry>();
const providerColumns = wrapCells([
  providerColumn.display({
    id: "provider",
    header: "Provider",
    meta: { rowHeader: true },
    cell: ({ row }) => (
      <span className="font-semibold">{providerLabels[row.original.provider]}</span>
    ),
  }),
  providerColumn.display({
    id: "status",
    header: "Status",
    cell: ({ row }) => (
      <span className="grid justify-items-start gap-1">
        <ProviderStatusBadge status={row.original.status} />
        {row.original.note === null ? null : (
          <span className="max-w-[28ch] text-xs text-muted-foreground">
            {noteLabels[row.original.note] ?? "See the runbook."}
          </span>
        )}
      </span>
    ),
  }),
  providerColumn.display({
    id: "mode",
    header: "Mode",
    cell: ({ row }) => (
      <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
        {modeLabels[row.original.mode]}
        {row.original.provider === "razorpay" ? (
          <EvidenceBadge evidence={paymentEvidenceLabel(row.original.mode)} />
        ) : null}
      </span>
    ),
  }),
  providerColumn.display({
    id: "calls",
    header: "Calls, failures",
    cell: ({ row }) => {
      const entry = row.original;
      return entry.calls === null ? (
        dash
      ) : (
        <>
          {entry.calls} calls, {entry.failures ?? 0} failed
          {entry.errorRate === null ? null : (
            <span className="text-xs text-muted-foreground">
              {" "}
              ({Math.round(entry.errorRate * 100)}%)
            </span>
          )}
        </>
      );
    },
  }),
  providerColumn.display({
    id: "refusals",
    header: "Security refusals",
    cell: ({ row }) =>
      row.original.securityRefusals === null ? (
        dash
      ) : (
        <span className="inline-flex flex-wrap items-center gap-x-2">
          {row.original.securityRefusals}
          {row.original.securityRefusals > 0 ? (
            <RunbookLink to={runbooks.webhookSignature}>Refusals</RunbookLink>
          ) : null}
        </span>
      ),
  }),
  providerColumn.display({
    id: "help",
    header: "Help",
    cell: ({ row }) => (
      <RunbookLink to={providerRunbook[row.original.provider] ?? runbooks.providerOutage}>
        Runbook
      </RunbookLink>
    ),
  }),
]);

/** 1. Provider health: the table, and under it the recent failures of each provider with the record each was about. */
export function ProviderHealthSection({
  health,
  capabilities,
  onOpen,
}: Readonly<{
  health: ReturnType<typeof useProviderHealth>;
  capabilities: DiagnosticsCapabilities;
  onOpen: (dialog: DialogRequest) => void;
}>) {
  return (
    <Section
      id="provider-health"
      titleId="diag-health"
      title="Provider health"
      runbookLinks={
        <RunbookLink to={runbooks.providerOutage}>Runbook: provider outage</RunbookLink>
      }
    >
      {health.isPending ? (
        <AsyncState
          kind="loading"
          title="Loading provider health"
          message="Waiting for the answer."
        />
      ) : health.isError ? (
        <AsyncState
          kind="error"
          title="Provider health could not be read"
          message={describeDiagnosticsError(health.error).message}
          onRetry={() => void health.refetch()}
        />
      ) : (
        <>
          <p className={hint} role="status">
            Updated {formatInstant(health.data.generatedAt)}. The window is{" "}
            {health.data.windowMinutes} minutes. “Unknown” is not a green light.
          </p>
          <DataTable<StaffProviderHealthEntry>
            caption="Health of each provider"
            data={health.data.providers}
            getRowId={(entry) => entry.provider}
            rowAttributes={(entry) => ({ "data-provider": entry.provider })}
            columns={providerColumns}
            banded="strong"
            className="rounded-md"
          />
          {health.data.providers
            .filter((entry) => entry.recentFailures.length > 0)
            .map((entry) => (
              <div
                key={entry.provider}
                data-failures-of={entry.provider}
                className="grid gap-2.5 rounded-md border border-hairline bg-pill p-4"
              >
                <h3 className="m-0 text-base font-semibold">
                  Recent failures of {providerLabels[entry.provider]}
                </h3>
                <ul
                  className="m-0 grid list-none gap-2 p-0"
                  aria-label={`Recent failures of ${providerLabels[entry.provider]}`}
                >
                  {entry.recentFailures.map((failure) => (
                    <li
                      key={`${failure.at.toISOString()}|${failure.code}|${failure.link?.id ?? ""}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                    >
                      <span>{formatInstant(failure.at)}</span> <code>{failure.code}</code>
                      {failure.link === null ? (
                        <span className="text-muted-foreground">No record is linked.</span>
                      ) : (
                        <>
                          {failure.link.type === "agent_run" ? (
                            <span className="record-link record-link--plain">
                              {linkTypeLabels[failure.link.type]}{" "}
                              <code title={failure.link.id}>{failure.link.id.slice(0, 8)}</code>
                            </span>
                          ) : (
                            <RecordLink type={failure.link.type} id={failure.link.id} />
                          )}
                          <span className="text-xs text-muted-foreground [&_code]:break-all">
                            Record id <code>{failure.link.id}</code>
                          </span>
                        </>
                      )}
                      {failure.runRef === null ? null : (
                        <span className="text-xs text-muted-foreground [&_code]:break-all">
                          Run <code>{failure.runRef}</code>
                        </span>
                      )}
                      {failure.link?.type === "background_intent" && capabilities.manage ? (
                        <Button
                          type="button"
                          variant="soft"
                          size="sm"
                          onClick={() =>
                            onOpen({
                              kind: "requeue",
                              jobId: (failure.link as { id: string }).id,
                              taskLabel: null,
                            })
                          }
                        >
                          <Icon name="refresh" />
                          Re-run{" "}
                          <span className="sr-only">
                            job {shortId((failure.link as { id: string }).id)} from{" "}
                            {providerLabels[entry.provider]}
                          </span>
                        </Button>
                      ) : null}
                      {failure.link?.type === "payment_request" && capabilities.reconcile ? (
                        <Button
                          type="button"
                          variant="soft"
                          size="sm"
                          onClick={() =>
                            onOpen({
                              kind: "reconcile",
                              paymentRequestId: (failure.link as { id: string }).id,
                            })
                          }
                        >
                          Reconcile{" "}
                          <span className="sr-only">
                            payment {shortId((failure.link as { id: string }).id)} from{" "}
                            {providerLabels[entry.provider]}
                          </span>
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </>
      )}
    </Section>
  );
}
